import {
  Check,
  ChevronRight,
  Film,
  Gift,
  MessageSquareText,
  Phone,
  Play,
  Quote,
  Sparkles,
  Usb,
  Video,
} from "lucide-react";
import { publishBookingSelection } from "@/lib/bookingSelectionBridge";

const SectionHeading = ({ kicker, title, sub }) => (
  <div className="text-center mb-12 md:mb-14 max-w-3xl mx-auto">
    <span className="tk-eyebrow">
      <span className="tk-divider" />
      {kicker}
      <span className="tk-divider" />
    </span>
    <h2 className="font-serif text-4xl sm:text-5xl mt-5 text-[var(--ink)] leading-tight">
      {title}
    </h2>
    {sub && <p className="mt-4 text-[var(--ink-soft)] leading-relaxed">{sub}</p>}
  </div>
);

export function ProductShowcase({ t }) {
  const icons = [Phone, Video];

  return (
    <section id="phones" className="py-20 md:py-28 tk-section-anchor">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <SectionHeading
          kicker={t.products.kicker}
          title={t.products.title}
          sub={t.products.sub}
        />
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {t.products.items.map((item, index) => {
            const Icon = icons[index] || Phone;
            return (
              <article key={item.name} className="tk-card overflow-hidden flex flex-col">
                <div className="min-h-64 bg-[var(--beige-100)] flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-6 rounded-full border border-dashed border-[rgba(110,30,43,0.2)]" />
                  <span className="w-32 h-32 rounded-full bg-white/80 shadow-xl flex items-center justify-center text-[var(--burgundy)]">
                    <Icon size={52} strokeWidth={1.35} />
                  </span>
                  <span className="absolute bottom-4 right-5 text-xs uppercase tracking-[0.2em] text-[var(--ink-soft)]">
                    {index === 0 ? "Audio" : "Video"}
                  </span>
                </div>
                <div className="p-7 md:p-9 flex flex-col flex-1">
                  <h3 className="font-serif text-3xl text-[var(--ink)]">{item.name}</h3>
                  <p className="mt-3 text-[var(--ink-soft)] leading-relaxed">{item.description}</p>
                  <ul className="mt-6 space-y-3 flex-1">
                    {item.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-sm text-[var(--ink)]">
                        <span className="mt-0.5 w-6 h-6 rounded-full bg-[var(--beige-100)] text-[var(--burgundy)] flex items-center justify-center flex-shrink-0">
                          <Check size={13} strokeWidth={2.5} />
                        </span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#booking"
                    className="tk-btn-primary justify-center mt-8"
                    onClick={() => publishBookingSelection({ deviceKind: index === 0 ? "audio" : "video" })}
                  >
                    {t.products.cta}
                    <ChevronRight size={16} />
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function PhoneCards({ t }) {
  return (
    <section className="py-20 md:py-28 bg-[var(--beige-100)] border-y border-[rgba(110,30,43,0.07)]">
      <div className="max-w-6xl mx-auto px-5 md:px-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-center">
        <div className="relative min-h-[360px] flex items-center justify-center" aria-hidden="true">
          {[0, 1, 2].map((card) => (
            <div
              key={card}
              className="absolute w-56 sm:w-64 aspect-[4/3] bg-white rounded-2xl border border-[rgba(110,30,43,0.12)] shadow-xl p-6"
              style={{
                transform: `translate(${(card - 1) * 34}px, ${(card - 1) * 8}px) rotate(${(card - 1) * 7}deg)`,
                zIndex: card + 1,
              }}
            >
              <span className="text-xs uppercase tracking-[0.22em] text-[var(--burgundy)]">
                Telefonka
              </span>
              <MessageSquareText className="mt-8 text-[var(--burgundy)]" size={32} />
              <div className="mt-5 h-px bg-[var(--beige-200)]" />
              <div className="mt-4 space-y-2">
                <div className="h-2 rounded-full bg-[var(--beige-200)] w-full" />
                <div className="h-2 rounded-full bg-[var(--beige-200)] w-4/5" />
              </div>
            </div>
          ))}
        </div>
        <div>
          <span className="tk-eyebrow">{t.cards.kicker}</span>
          <h2 className="font-serif text-4xl sm:text-5xl mt-5 text-[var(--ink)] leading-tight">
            {t.cards.title}
          </h2>
          <p className="mt-5 text-[var(--ink-soft)] leading-relaxed">{t.cards.body}</p>
          <ul className="mt-7 grid sm:grid-cols-2 gap-4">
            {t.cards.features.map((feature) => (
              <li key={feature} className="tk-card p-4 flex items-start gap-3">
                <Gift size={18} className="text-[var(--burgundy)] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-[var(--ink)]">{feature}</span>
              </li>
            ))}
          </ul>
          <a
            href="#booking"
            className="tk-btn-secondary mt-8"
            onClick={() =>
              publishBookingSelection({ extraId: "cards" })
            }
          >
            {t.cards.cta}
          </a>
        </div>
      </div>
    </section>
  );
}

export function Extras({ t }) {
  const icons = [Usb, Film];

  return (
    <section id="extras" className="py-20 md:py-28 tk-section-anchor">
      <div className="max-w-6xl mx-auto px-5 md:px-10">
        <SectionHeading kicker={t.extras.kicker} title={t.extras.title} />
        <div className="grid md:grid-cols-2 gap-6">
          {t.extras.items.map((item, index) => {
            const Icon = icons[index] || Sparkles;
            return (
              <article
                key={item.name}
                className="tk-card p-7 md:p-9"
                style={item.highlight ? { background: "var(--burgundy)", color: "#fff", borderColor: "transparent" } : undefined}
              >
                <span
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                    item.highlight
                      ? "bg-white/10 text-[var(--beige-100)]"
                      : "bg-[var(--beige-100)] text-[var(--burgundy)]"
                  }`}
                >
                  <Icon size={24} />
                </span>
                {item.highlight && (
                  <p className="mt-7 text-xs uppercase tracking-[0.2em] text-[var(--beige-200)]">
                    {item.highlight}
                  </p>
                )}
                <h3 className="font-serif text-3xl mt-4">{item.name}</h3>
                <p
                  className={`mt-4 leading-relaxed ${
                    item.highlight ? "text-white/80" : "text-[var(--ink-soft)]"
                  }`}
                >
                  {item.description}
                </p>
                <a
                  href="#booking"
                  onClick={index === 0 ? () => publishBookingSelection({ extraId: "usb" }) : undefined}
                  className={`inline-flex min-h-11 items-center gap-2 mt-7 font-medium ${
                    item.highlight
                      ? "text-[var(--beige-100)]"
                      : "text-[var(--burgundy)]"
                  }`}
                >
                  {t.extras.cta}
                  <ChevronRight size={16} />
                </a>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function VideoShowcase({ t }) {
  return (
    <section id="video" className="py-20 md:py-28 tk-section-anchor">
      <div className="max-w-5xl mx-auto px-5 md:px-10">
        <SectionHeading kicker={t.video.kicker} title={t.video.title} sub={t.video.body} />
        <div
          className="tk-card aspect-video text-white overflow-hidden relative flex items-center justify-center"
          style={{ background: "var(--ink)" }}
        >
          <div className="absolute inset-0 opacity-20 tk-video-grid" />
          <div className="relative z-10 text-center px-6">
            <span className="w-20 h-20 mx-auto rounded-full border border-white/30 bg-white/10 flex items-center justify-center">
              <Play size={30} fill="currentColor" />
            </span>
            <p className="mt-6 text-sm uppercase tracking-[0.2em] text-white/70">
              {t.video.placeholder}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Testimonials({ t }) {
  return (
    <section className="py-20 md:py-28 bg-[var(--beige-100)] border-y border-[rgba(110,30,43,0.07)]">
      <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
        <SectionHeading kicker={t.testimonials.kicker} title={t.testimonials.title} />
        <div className="tk-card p-8 md:p-12">
          <Quote size={34} className="mx-auto text-[var(--burgundy)]" />
          <div className="mt-5 flex justify-center gap-1 text-[var(--burgundy)]" aria-hidden="true">
            {"☆☆☆☆☆".split("").map((star, index) => (
              <span key={index}>{star}</span>
            ))}
          </div>
          <p className="mt-5 text-[var(--ink-soft)] leading-relaxed">
            {t.testimonials.empty}
          </p>
        </div>
      </div>
    </section>
  );
}

export function Team({ t }) {
  const isHungarian = t.team.kicker === "Rólunk";
  const teamCopy = isHungarian
    ? {
        sub:
          "A Telefonka egy közös ötletből és abból a vágyból született, hogy az esküvők legszemélyesebb pillanatai ne csak képeken, hanem hangokban és történetekben is megmaradjanak. Hárman dolgozunk azon, hogy a Telefonka az első ötlettől egészen az elkészült hang- és videóemlékekig különleges és szerethető élmény legyen.",
        members: [
          {
            name: "Anna",
            role: "Grafikus • A Telefonka megálmodója",
            description:
              "Anna álmodta meg a Telefonka világát és vizuális megjelenését. Ő felel azért, hogy minden részlet – a készülékektől a kártyákon át a teljes arculatig – egységes és szerethető legyen.",
          },
          {
            name: "Csabi",
            role: "Üzletfejlesztés • Háttérfolyamatok",
            description:
              "Csabi a Telefonka működésének és fejlődésének hátterén dolgozik. Az üzletfejlesztés, a digitális megoldások és a háttérfolyamatok összehangolásával gondoskodik arról, hogy az ötletekből működő szolgáltatás legyen.",
          },
          {
            name: "Gellért",
            role: "Hang- és videóprodukció • Utómunka",
            description:
              "Gellért gondoskodik arról, hogy a vendégek által hátrahagyott pillanatok a lehető legszebb formában maradjanak meg. A hang- és videóanyagok feldolgozásával, rendezésével és utómunkájával válik a nyers felvételekből valódi emlék.",
          },
        ],
      }
    : {
        sub:
          "Telefonka grew from a shared idea and a desire to preserve the most personal moments of weddings not only in photographs, but in voices and stories too. The three of us work together to make every step, from the first idea to the finished audio and video memories, feel thoughtful, personal and memorable.",
        members: [
          {
            name: "Anna",
            role: "Graphic designer • Creator of Telefonka",
            description:
              "Anna created Telefonka’s visual world. She shapes every detail – from the phones and cards to the complete brand identity – so the experience feels consistent, distinctive and warm.",
          },
          {
            name: "Csabi",
            role: "Business development • Operations",
            description:
              "Csabi works behind the scenes on Telefonka’s growth and day-to-day operation. He brings business development, digital solutions and internal processes together to turn ideas into a smoothly working service.",
          },
          {
            name: "Gellért",
            role: "Audio & video production • Post-production",
            description:
              "Gellért makes sure the moments left by guests are preserved in their best form. Through editing, organising and post-producing the recordings, he turns raw audio and video into lasting memories.",
          },
        ],
      };

  return (
    <section id="team" className="py-20 md:py-28 tk-section-anchor">
      <div className="max-w-6xl mx-auto px-5 md:px-10">
        <SectionHeading kicker={t.team.kicker} title={t.team.title} sub={teamCopy.sub} />
        <div className="grid lg:grid-cols-3 gap-7 items-stretch">
          {teamCopy.members.map((member) => (
            <article key={member.name} className="tk-card p-7 md:p-8 flex flex-col text-center">
              <div className="w-24 h-24 mx-auto rounded-full border border-[rgba(110,30,43,0.16)] bg-[var(--beige-100)] flex items-center justify-center font-serif text-4xl text-[var(--burgundy)]">
                {member.name.charAt(0)}
              </div>
              <h3 className="font-serif text-4xl mt-6 text-[var(--ink)]">{member.name}</h3>
              <p className="mt-2 text-xs sm:text-sm uppercase tracking-[0.12em] text-[var(--burgundy)] leading-relaxed">
                {member.role}
              </p>
              <div className="w-10 h-px bg-[var(--burgundy)]/30 mx-auto my-5" aria-hidden="true" />
              <p className="text-sm text-[var(--ink-soft)] leading-relaxed flex-1">
                {member.description}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ClosingCTA({ t }) {
  return (
    <section className="py-20 md:py-28 bg-[var(--burgundy)] text-white">
      <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-white/10 flex items-center justify-center">
          <Phone size={26} />
        </span>
        <h2 className="font-serif text-4xl sm:text-6xl mt-7 leading-tight">
          {t.closing.title}
        </h2>
        <p className="mt-5 text-white/80 text-lg leading-relaxed max-w-2xl mx-auto">
          {t.closing.body}
        </p>
        <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="#booking"
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[var(--beige-100)] text-[var(--burgundy)] px-7 font-medium hover:bg-white transition"
          >
            {t.closing.cta}
            <ChevronRight size={16} />
          </a>
          <p className="text-sm text-white/75">{t.closing.contact}</p>
        </div>
      </div>
    </section>
  );
}
